// LLM / MCP 向けの文書要約とプレビュー。
import { useDocumentStore } from '@/stores/documentStore'
import { layerRows } from '@/model/layerTree'
import { exportPixels } from '@/app/engine'
import { resampleRgba, encodeRgba } from '@/io/imageEncode'
import { isFontAvailable } from '@/text/fonts'

export interface LayerSummary {
  id: string
  name: string
  kind: string
  depth: number
  visible: boolean
  opacity: number
  blendMode: string
  clipToBelow: boolean
  hasMask: boolean
  locked: boolean
  /** ラスターの内容矩形 (文書座標)。空なら null */
  bounds: { x: number; y: number; w: number; h: number } | null
  active: boolean
  adjustment?: unknown
  fill?: unknown
  text?: { text: string; style: unknown; position: { x: number; y: number }; boxWidth?: number; fontAvailable: boolean }
  styles?: unknown
  shape?: unknown
}

export interface DocumentSummary {
  name: string
  width: number
  height: number
  background: string
  layers: LayerSummary[]
  selection: { x: number; y: number; w: number; h: number } | null
  channels: { id: string; name: string }[]
  patterns: { id: string; name: string; w: number; h: number }[]
  comps: { id: string; name: string }[]
  activeLayerIds: string[]
  canUndo: boolean
  canRedo: boolean
  dirty: boolean
}

export function describeDocument(): DocumentSummary | null {
  const store = useDocumentStore()
  const doc = store.doc
  if (!doc) return null
  const sel = store.selectionMask()
  const bg = doc.meta.background
  return {
    name: doc.meta.name,
    width: doc.meta.width,
    height: doc.meta.height,
    background: typeof bg === 'string' ? bg : bg.hex,
    layers: layerRows(doc).map(r => ({
      id: r.id,
      name: r.layer.name,
      kind: r.layer.kind,
      depth: r.depth,
      visible: r.layer.visible,
      opacity: r.layer.opacity,
      blendMode: r.layer.blendMode,
      clipToBelow: r.layer.clipToBelow,
      hasMask: !!r.layer.mask,
      locked: r.layer.lock.all || r.layer.lock.pixels || r.layer.lock.position,
      bounds: r.layer.kind === 'raster' || r.layer.kind === 'text' || r.layer.kind === 'shape' || r.layer.kind === 'smart' ? store.layerRect(r.id) : null,
      active: doc.activeLayerIds.includes(r.id),
      ...(r.layer.kind === 'adjustment' ? { adjustment: r.layer.adjustment } : {}),
      ...(r.layer.kind === 'fill' ? { fill: r.layer.fill } : {}),
      // fontAvailable: false なら別の書体で描かれている (指定名が黙って無視される)
      ...(r.layer.kind === 'text' ? { text: { text: r.layer.text, style: r.layer.style, position: r.layer.position, boxWidth: r.layer.boxWidth, fontAvailable: isFontAvailable(r.layer.style.fontFamily) } } : {}),
      ...(r.layer.styles ? { styles: r.layer.styles } : {}),
      ...(r.layer.blendIf ? { blendIf: r.layer.blendIf } : {}),
      ...(r.layer.kind === 'smart' ? { smart: { sourceRect: r.layer.sourceRect, transform: r.layer.transform, interpolation: r.layer.interpolation } } : {}),
      ...(r.layer.kind === 'shape' ? { shape: { geometry: r.layer.geometry, fillHex: r.layer.fillHex, strokeHex: r.layer.strokeHex, strokeWidth: r.layer.strokeWidth, transform: r.layer.transform } } : {})
    })),
    selection: sel ? { ...sel.rect } : null,
    channels: doc.channels.map(c => ({ id: c.id, name: c.name })),
    patterns: doc.patterns.map(p => ({ id: p.id, name: p.name, w: p.w, h: p.h })),
    comps: doc.comps.map(c => ({ id: c.id, name: c.name })),
    activeLayerIds: [...doc.activeLayerIds],
    canUndo: store.canUndo,
    canRedo: store.canRedo,
    dirty: store.isDirty
  }
}

/** 合成結果の縮小プレビューを PNG (base64) で返す */
export async function previewPng(maxSize = 512): Promise<{ width: number; height: number; base64: string } | null> {
  const store = useDocumentStore()
  if (!store.doc) return null
  let px = exportPixels()
  const scale = Math.min(1, maxSize / Math.max(px.width, px.height))
  if (scale < 1) px = resampleRgba(px.width, px.height, px.rgba, scale)
  const blob = await encodeRgba(px.width, px.height, px.rgba, { format: 'png' })
  const buf = new Uint8Array(await blob.arrayBuffer())
  let bin = ''
  for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000))
  return { width: px.width, height: px.height, base64: btoa(bin) }
}
